import { DataRetentionCategory, DataRetentionPeriodDays, DataRetentionRules } from '@/types';

export type RetentionSelectValue = number | 'indefinite';

export interface DataRetentionCategoryConfiguration {
    category: DataRetentionCategory;
    description: string;
    dropdownLabel: string;
    label: string;
    learnMoreTopic: string;
    maximumDays: number;
    minimumDays: number;
    recommendedMinimumDays?: number;
}

export interface RetentionPeriodOption {
    description: string;
    label: string;
    value: RetentionSelectValue;
}

export const DEFAULT_DATA_RETENTION_RULES: DataRetentionRules = {
    activityHistory: 2_555,
    auditLogs: 2_555,
    trash: 30,
    versionHistory: 2_555,
};

export const DATA_RETENTION_CATEGORY_CONFIGURATIONS: readonly DataRetentionCategoryConfiguration[] = [
    {
        category: 'trash',
        description: 'Permanently remove soft-deleted workspace resources after this recovery window.',
        dropdownLabel: 'Trash Retention Period',
        label: 'Items in Trash',
        learnMoreTopic: 'Trash retention and recovery windows',
        maximumDays: 365,
        minimumDays: 7,
    },
    {
        category: 'auditLogs',
        description: 'Preserve security, access, administrative, and policy-change evidence.',
        dropdownLabel: 'Audit Log Retention Period',
        label: 'Audit Logs',
        learnMoreTopic: 'audit log retention',
        maximumDays: 3_650,
        minimumDays: 365,
        recommendedMinimumDays: 2_190,
    },
    {
        category: 'activityHistory',
        description: 'Retain workspace resource activity used for traceability and collaboration history.',
        dropdownLabel: 'Activity History Retention Period',
        label: 'Activity History',
        learnMoreTopic: 'activity history retention',
        maximumDays: 3_650,
        minimumDays: 30,
        recommendedMinimumDays: 1_825,
    },
    {
        category: 'versionHistory',
        description: 'Retain historical resource snapshots used for review, comparison, and recovery.',
        dropdownLabel: 'Version History Retention Period',
        label: 'Version History',
        learnMoreTopic: 'version history retention',
        maximumDays: 3_650,
        minimumDays: 30,
        recommendedMinimumDays: 1_825,
    },
];

const RETENTION_PERIOD_OPTIONS: readonly RetentionPeriodOption[] = [
    { description: 'One week after deletion.', label: '7 Days', value: 7 },
    { description: 'Two weeks after deletion.', label: '14 Days', value: 14 },
    { description: 'One month.', label: '30 Days', value: 30 },
    { description: 'Three months.', label: '90 Days', value: 90 },
    { description: 'Six months.', label: '180 Days', value: 180 },
    { description: 'One year.', label: '1 Year', value: 365 },
    { description: 'Three years.', label: '3 Years', value: 1_095 },
    { description: 'Five years.', label: '5 Years', value: 1_825 },
    { description: 'Six years.', label: '6 Years', value: 2_190 },
    { description: 'Seven years.', label: '7 Years', value: 2_555 },
    { description: 'Ten years.', label: '10 Years', value: 3_650 },
    { description: 'Do not automatically delete this category.', label: 'Retain Indefinitely', value: 'indefinite' },
];

export const retentionOptionsForCategory = (
    configuration: DataRetentionCategoryConfiguration,
): RetentionPeriodOption[] =>
    RETENTION_PERIOD_OPTIONS.filter(
        (option) =>
            option.value === 'indefinite' ||
            (option.value >= configuration.minimumDays && option.value <= configuration.maximumDays),
    );

export const toRetentionSelectValue = (days: DataRetentionPeriodDays): RetentionSelectValue =>
    days === null ? 'indefinite' : days;

export const fromRetentionSelectValue = (value: RetentionSelectValue): DataRetentionPeriodDays =>
    value === 'indefinite' ? null : value;

/**
 * Ant Design registers nested form fields incrementally, so rules can be absent or
 * partial during an initial render. Preserve explicit `null` values because they
 * represent indefinite retention while filling only unregistered values.
 */
export const normalizeDataRetentionRules = (rules?: Partial<DataRetentionRules> | null): DataRetentionRules => ({
    activityHistory:
        rules?.activityHistory === undefined ? DEFAULT_DATA_RETENTION_RULES.activityHistory : rules.activityHistory,
    auditLogs: rules?.auditLogs === undefined ? DEFAULT_DATA_RETENTION_RULES.auditLogs : rules.auditLogs,
    trash: rules?.trash === undefined ? DEFAULT_DATA_RETENTION_RULES.trash : rules.trash,
    versionHistory:
        rules?.versionHistory === undefined ? DEFAULT_DATA_RETENTION_RULES.versionHistory : rules.versionHistory,
});

export const hasBelowBaselineRetention = (rules?: Partial<DataRetentionRules> | null): boolean => {
    const normalizedRules = normalizeDataRetentionRules(rules);

    return DATA_RETENTION_CATEGORY_CONFIGURATIONS.some(({ category, recommendedMinimumDays }) => {
        const value = normalizedRules[category];

        return value !== null && recommendedMinimumDays !== undefined && value < recommendedMinimumDays;
    });
};

export const preservesVersionHistoryInvariant = (rules?: Partial<DataRetentionRules> | null): boolean => {
    const normalizedRules = normalizeDataRetentionRules(rules);

    if (normalizedRules.activityHistory === null) {
        return true;
    }

    if (normalizedRules.versionHistory === null) {
        return false;
    }

    return normalizedRules.activityHistory >= normalizedRules.versionHistory;
};

export const formatRetentionPeriod = (days: DataRetentionPeriodDays): string => {
    const matchingOption = RETENTION_PERIOD_OPTIONS.find((option) =>
        days === null ? option.value === 'indefinite' : option.value === days,
    );

    return matchingOption?.label ?? `${days} Days`;
};
